import { useState, useCallback } from 'react';
import type { SupplyProgram } from '@shared/types';
import { isSupplyProgram } from '@shared/validation';

const STORAGE_KEY = 'supply-selected-program';

/**
 * Which supply catalog the person on this device is requesting out of.
 *
 * Sticky per device, like the station: whoever submits cleaning requests is
 * generally not the same person who submits kitchen ones, so asking every time
 * would be a tap they don't need.
 *
 * This is only a *view* choice. Nothing about it is authoritative — the worker
 * derives an order's program from each line's catalog item — so if we ever drop
 * this picker in favour of one mixed request form, this hook and the screen that
 * uses it are all that come out. See docs/ARCHITECTURE.md §4.
 */
export function useProgram() {
  const [program, setProgram] = useState<SupplyProgram | null>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      // Guard the read: a stale or hand-edited value must not reach the API.
      return isSupplyProgram(saved) ? saved : null;
    } catch {
      return null;
    }
  });

  const selectProgram = useCallback((p: SupplyProgram) => {
    setProgram(p);
    try {
      localStorage.setItem(STORAGE_KEY, p);
    } catch {
      // Private mode / full storage: the choice just won't stick.
    }
  }, []);

  const clearProgram = useCallback(() => {
    setProgram(null);
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
  }, []);

  return { program, selectProgram, clearProgram };
}
